import { clamp } from '../core/math';
import type { KartState } from '../physics/vehicle';
import type { Livery } from './liveries';
import type { Camera } from './camera';

/** A little larger than a real kart (1.9m x 1.4m) so it reads at a playable
 *  zoom, but not by much — the margin used to be closer to a quarter of the
 *  kart's own size, which is what set the collision radius in collision.ts
 *  too far from the real footprint. */
export const LEN = 2.1;
export const WID = 1.45;

/** Everything drawKart actually needs — a KartState satisfies this structurally. */
export interface KartPose {
  x: number;
  y: number;
  heading: number;
  steer: number;
}

/** Multiply a #rrggbb colour toward soot as damage climbs. */
function damageTint(hex: string, damage: number): string {
  if (damage >= 0.999) return hex;
  const v = parseHex(hex);
  const f = 1 - (1 - damage) * 0.62;
  return `rgb(${Math.round(v[0] * f)},${Math.round(v[1] * f)},${Math.round(v[2] * f)})`;
}

function parseHex(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function drawKart(
  ctx: CanvasRenderingContext2D,
  s: KartPose,
  livery: Livery,
  alpha = 1,
  damage = 1,
): void {
  ctx.save();
  ctx.translate(s.x, s.y);
  ctx.rotate(s.heading);
  ctx.globalAlpha = alpha;

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.ellipse(0.06, 0.1, LEN * 0.46, WID * 0.42, 0, 0, Math.PI * 2);
  ctx.fill();

  // Tyres. The fronts turn with the steering, which is the clearest visual cue
  // for how much lock is being asked of the kart.
  ctx.fillStyle = '#1a1b1e';
  const tyreL = 0.52;
  const tyreW = 0.3;
  for (const [ax, ay, steered] of [
    [-0.62, -WID / 2, false],
    [-0.62, WID / 2, false],
    [0.66, -WID / 2 + 0.06, true],
    [0.66, WID / 2 - 0.06, true],
  ] as Array<[number, number, boolean]>) {
    ctx.save();
    ctx.translate(ax, ay);
    if (steered) ctx.rotate(s.steer);
    ctx.fillRect(-tyreL / 2, -tyreW / 2, tyreL, tyreW);
    ctx.restore();
  }

  // Chassis — darkened as damage mounts, so a battered kart reads at a glance.
  ctx.fillStyle = damageTint(livery.body, damage);
  ctx.beginPath();
  ctx.moveTo(-1.05, -0.42);
  ctx.lineTo(0.5, -0.36);
  ctx.lineTo(0.92, -0.5);
  ctx.lineTo(1.16, -0.3);
  ctx.lineTo(1.16, 0.3);
  ctx.lineTo(0.92, 0.5);
  ctx.lineTo(0.5, 0.36);
  ctx.lineTo(-1.05, 0.42);
  ctx.closePath();
  ctx.fill();

  // Sidepods
  ctx.fillStyle = damageTint(livery.accent, damage);
  ctx.fillRect(-0.5, -0.72, 0.8, 0.2);
  ctx.fillRect(-0.5, 0.52, 0.8, 0.2);

  // Seat and helmet
  ctx.fillStyle = '#16171a';
  ctx.beginPath();
  ctx.ellipse(-0.34, 0, 0.42, 0.34, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = damageTint(livery.helmet, damage);
  ctx.beginPath();
  ctx.arc(-0.22, 0, 0.26, 0, Math.PI * 2);
  ctx.fill();

  // Scuffs: a few charcoal streaks that sharpen as damage deepens, so the
  // degradation is visible even in the small top-down sprite.
  if (damage < 0.999) {
    ctx.strokeStyle = `rgba(20,22,25,${0.3 + damage * 0.3})`;
    ctx.lineWidth = 0.06;
    ctx.beginPath();
    ctx.moveTo(0.2, -0.4);
    ctx.lineTo(0.6, -0.28);
    ctx.moveTo(0.3, 0.34);
    ctx.lineTo(0.75, 0.3);
    ctx.moveTo(-0.7, -0.2);
    ctx.lineTo(-0.35, -0.3);
    ctx.stroke();
  }

  ctx.restore();
  ctx.globalAlpha = 1;
}

/** Paint a small nose-up top-down sprite of a livery into a chip canvas, using
 *  the same {@link drawKart} the game renders with so the preview stays honest.
 *  Sizes the backing store to the device pixel ratio and lays the CSS box out
 *  in the given points. */
export function renderLiveryPreview(
  canvas: HTMLCanvasElement,
  livery: Livery,
  cssW = 34,
  cssH = 40,
): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.scale(dpr, dpr);
  ctx.translate(cssW / 2, cssH / 2 + 1);
  const scale = Math.min(cssW / (WID + 0.5), cssH / (LEN + 0.6));
  ctx.scale(scale, scale);
  drawKart(ctx, { x: 0, y: 0, heading: -Math.PI / 2, steer: 0 }, livery);
  ctx.restore();
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  r: number;
  g: number;
  b: number;
}

export interface Skid {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  alpha: number;
}

const MAX_SKIDS = 900;

export class Effects {
  private particles: Particle[] = [];
  private skids: Skid[] = [];
  private lastSkid: { x: number; y: number } | null = null;

  /** Read-only access for renderers other than the Canvas2D one — the 3D
   *  renderer draws these same particles and skid marks, not a second sim. */
  get skidMarks(): readonly Skid[] {
    return this.skids;
  }

  get particleList(): readonly Particle[] {
    return this.particles;
  }

  clear(): void {
    this.particles.length = 0;
    this.skids.length = 0;
    this.lastSkid = null;
  }

  /**
   * Locked wheels and heavy slip lay down rubber. The marks persist for the run,
   * which turns a driver's own mistakes into track furniture they can see on the
   * next lap — a free feedback loop for learning braking points.
   */
  markSkid(s: KartState, intensity: number): void {
    if (intensity < 0.15) {
      this.lastSkid = null;
      return;
    }
    if (this.lastSkid) {
      const d = Math.hypot(s.x - this.lastSkid.x, s.y - this.lastSkid.y);
      if (d < 0.25) return;
      this.skids.push({
        x0: this.lastSkid.x,
        y0: this.lastSkid.y,
        x1: s.x,
        y1: s.y,
        alpha: clamp(intensity, 0, 1) * 0.5,
      });
      if (this.skids.length > MAX_SKIDS) this.skids.shift();
    }
    this.lastSkid = { x: s.x, y: s.y };
  }

  emitSmoke(x: number, y: number, vx: number, vy: number, amount: number): void {
    for (let i = 0; i < amount; i++) {
      const spread = 1.6;
      this.particles.push({
        x,
        y,
        vx: vx * 0.12 + (Math.random() - 0.5) * spread,
        vy: vy * 0.12 + (Math.random() - 0.5) * spread,
        life: 0,
        maxLife: 0.55 + Math.random() * 0.4,
        size: 0.35 + Math.random() * 0.4,
        r: 210,
        g: 210,
        b: 214,
      });
    }
  }

  /** Orange sparks flung from barrier contact. Short, bright, and thrown clear
   *  of the wall rather than left to hang where the kart hit. */
  emitSparks(x: number, y: number, vx: number, vy: number, amount: number): void {
    for (let i = 0; i < amount; i++) {
      this.particles.push({
        x,
        y,
        vx: -vx * 0.4 + (Math.random() - 0.5) * 7,
        vy: -vy * 0.4 + (Math.random() - 0.5) * 7,
        life: 0,
        maxLife: 0.25 + Math.random() * 0.2,
        size: 0.16 + Math.random() * 0.2,
        r: 255,
        g: 176,
        b: 60,
      });
    }
  }

  emitDust(x: number, y: number, vx: number, vy: number, amount: number): void {
    for (let i = 0; i < amount; i++) {
      this.particles.push({
        x,
        y,
        vx: -vx * 0.18 + (Math.random() - 0.5) * 2.2,
        vy: -vy * 0.18 + (Math.random() - 0.5) * 2.2,
        life: 0,
        maxLife: 0.5 + Math.random() * 0.35,
        size: 0.25 + Math.random() * 0.35,
        r: 128,
        g: 116,
        b: 74,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += dt;
      if (p.life >= p.maxLife) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 1 - 2.2 * dt;
      p.vy *= 1 - 2.2 * dt;
      p.size += dt * 0.9;
    }
  }

  draw(ctx: CanvasRenderingContext2D, cam: Camera): void {
    ctx.save();
    cam.applyTransform(ctx);

    ctx.strokeStyle = '#101114';
    ctx.lineCap = 'round';
    ctx.lineWidth = 0.42;
    for (const s of this.skids) {
      ctx.globalAlpha = s.alpha;
      ctx.beginPath();
      ctx.moveTo(s.x0, s.y0);
      ctx.lineTo(s.x1, s.y1);
      ctx.stroke();
    }

    for (const p of this.particles) {
      const t = p.life / p.maxLife;
      ctx.globalAlpha = (1 - t) * 0.5;
      ctx.fillStyle = `rgb(${p.r},${p.g},${p.b})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }
}
